import { useState, useEffect, useCallback, useRef } from 'react';
import { Paperclip, Upload, Trash2 } from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import { attachmentService } from '../services/attachmentService';
import { ATTACHMENT_CATEGORY_LABELS } from '../types/billValidationTypes';
import type { BvAttachment, BvAttachmentCategory } from '../types/billValidationTypes';

export function AttachmentsPage() {
  const [attachments, setAttachments] = useState<BvAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState<BvAttachmentCategory>('other');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await attachmentService.list({});
    if (r.success) setAttachments(r.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const r = await attachmentService.upload(file, { category });
    if (r.success) await load();
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = async (a: BvAttachment) => {
    if (!confirm(`Delete "${a.file_name}"?`)) return;
    await attachmentService.delete(a.id, a.storage_path);
    await load();
  };

  const handleDownload = async (a: BvAttachment) => {
    const url = await attachmentService.getSignedUrl(a.storage_path);
    if (url) window.open(url, '_blank');
  };

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Attachments</h1>
          <p className="text-sm text-gray-500 mt-0.5">LOAs, contracts, and other documents</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={category} onValueChange={(v) => setCategory(v as BvAttachmentCategory)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ATTACHMENT_CATEGORY_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            <Upload className="h-4 w-4 mr-1.5" />
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
          <input ref={fileRef} type="file" className="hidden" onChange={handleFileChange} />
        </div>
      </div>

      <div className="flex-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : attachments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Paperclip className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">No attachments yet</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500 font-medium uppercase tracking-wider">
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">LOA Expiry</th>
                <th className="px-4 py-3">Uploaded</th>
                <th className="px-4 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {attachments.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-medium text-gray-800">{a.file_name}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="text-xs">
                      {ATTACHMENT_CATEGORY_LABELS[a.category]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {a.loa_expiry_date
                      ? new Date(a.loa_expiry_date).toLocaleDateString('en-AU')
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {new Date(a.created_at).toLocaleDateString('en-AU')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => handleDownload(a)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Open
                      </button>
                      <button
                        onClick={() => handleDelete(a)}
                        className="p-0.5 hover:text-red-600 text-gray-400"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
